import { useState, useRef, useEffect, FC, useMemo } from 'react'

import { Skeleton } from 'antd'
import axios from 'axios'

import { DottedLink } from 'src/Components/DottedLink'

import {
  DescriptionWrapper,
  ErrorTitle,
  LinkTiktok,
  PlayerError,
  PlayerWrapper,
  Wrapper,
} from './styles'

export const TIKTOK_OEMBED_BASE_URL = 'https://www.tiktok.com/oembed'

interface TikTokResponse {
  html: string
  status_msg: string
}

export interface TikTokProps {
  url?: string
}

export const TikTokPlayer: FC<TikTokProps> = ({ url }) => {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<Record<string, string> | null>(null)
  const [html, setHTML] = useState<string>()

  const ref = useRef(null)

  useEffect(() => {
    if (!('MutationObserver' in window)) {
      return setLoaded(true)
    }
    const elem = ref.current

    const observer = new MutationObserver((mutationList) => {
      const iframeAdded = mutationList.reduce<Node | null>(
        (accumulator, value) => {
          const iframe = Array.from(value.addedNodes).find(
            (node) => node.nodeName === 'IFRAME',
          )
          if (iframe) {
            accumulator = iframe
          }
          return accumulator
        },
        null,
      )

      if (iframeAdded) {
        iframeAdded.addEventListener('load', () => setLoaded(true))
      }
    })

    if (elem) {
      observer.observe(elem, {
        childList: true,
        attributes: true,
        subtree: true,
      })
    }
    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const getTikTokEmbed = async (): Promise<void> => {
      try {
        const { data } = await axios.get<TikTokResponse>(
          `${TIKTOK_OEMBED_BASE_URL}?url=${url}`,
        )
        if (data && data.status_msg) {
          throw new Error(data.status_msg)
        }

        if (!data || !data.html) {
          throw new Error("API response doesn't look right")
        }

        const htmlString = data.html

        const tempElement = document.createElement('div')
        tempElement.innerHTML = htmlString

        const [scriptTag] = tempElement.getElementsByTagName('script')
        const script = document.createElement('script')
        script.src = scriptTag.src
        script.async = true
        script.id = 'ttEmbedder'
        document.head.appendChild(script)

        setHTML(htmlString.substring(0, htmlString.indexOf('<script')))
      } catch (err) {
        setError(err as Record<string, string>)
      }
    }
    if (url) {
      void getTikTokEmbed()
    }
  }, [url])

  const memoHTMLObject = useMemo(() => ({ __html: html || '' }), [html])

  if (error) {
    return (
      <PlayerError>
        <ErrorTitle>Preview not available</ErrorTitle>
        <DescriptionWrapper>
          <span>or open video on </span>
          <LinkTiktok target='_blank' href={url}>
            TikTok
          </LinkTiktok>
        </DescriptionWrapper>
      </PlayerError>
    )
  }

  return (
    <Wrapper>
      {!loaded && (
        <Skeleton.Node
          active={true}
          style={{ height: '565px', width: '300px', borderRadius: '16px' }}
        >
          <div data-testid='player-skeleton' />
        </Skeleton.Node>
      )}
      <PlayerWrapper
        ref={ref}
        style={{ display: loaded && html ? 'flex' : 'none' }}
        dangerouslySetInnerHTML={memoHTMLObject}
      />
      {!error && (
        <DescriptionWrapper>
          <DottedLink target='_blank' href={url}>
            Open video on TikTok
          </DottedLink>
        </DescriptionWrapper>
      )}
    </Wrapper>
  )
}
